import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Estados_Habitaciones, useCheckoutMutation, useCurrentDateLazyQuery } from "src/gql/schema"
import AuthRequiredModal from "src/pages/inventario/modals/Auth/AuthRequiredModal/AuthRequiredModal"
import { useProfile } from "src/shared/hooks/useProfile"
import useSnackbar from "src/shared/hooks/useSnackbar"
import useLoadingState from "src/shared/hooks/useLoadingState"
import { RoleNames } from "src/shared/hooks/useAuth"

// Roles con acceso a cambiar el estado de la habitación, son los que pueden autorizar la liberación
const LIBERAR_AUTHORIZED_ROLES = [RoleNames.admin, RoleNames.superadmin, RoleNames.gerente, RoleNames.recepcionista]

// Acciones de una habitación en venta en línea compartidas entre el detalle y las cards:
// check in de la última renta y liberación (no show) con autorización
export const useVentaEnLineaAcciones = ({ onLiberada }: { onLiberada?: () => void } = {}) => {
    const navigate = useNavigate()
    const { usuario_id, rolName } = useProfile()
    const { showSnackbar } = useSnackbar()
    const { isLoading, toggleIsLoading, tryStartLoading } = useLoadingState()
    const [checkout] = useCheckoutMutation()
    const [getCurrentDate] = useCurrentDateLazyQuery()
    const [habitacionALiberar, setHabitacionALiberar] = useState<any>(null)

    const canOperate =
        rolName !== RoleNames.mantenimiento &&
        rolName !== RoleNames.valet &&
        rolName !== RoleNames.roomService &&
        rolName !== RoleNames.monitoreo &&
        rolName !== RoleNames.coordinador

    // Se envía la última renta con la forma de una reserva para reutilizar el flujo de check in de venta de habitación
    const checkIn = (room: any) => {
        const renta = room?.ultima_renta
        navigate(`/u/venta-habitacion/${room?.habitacion_id}`, {
            state: {
                ventaEnLinea: true,
                reservaSeleccionada: {
                    renta_id: renta?.renta_id,
                    reserva_id: renta?.reserva_id,
                    folio: renta?.folio,
                    tarifa_id: renta?.tarifa_id,
                    tarifa: {
                        ...renta?.tarifa,
                        tarifa_id: renta?.tarifa_id,
                        tipo_alojamiento: renta?.tarifa?.tipo_alojamiento || renta?.tipo_alojamiento,
                    },
                    nombre_huesped: renta?.nombre_huesped || renta?.reserva?.nombre_huesped || "",
                    numero_personas: renta?.numero_personas || 0,
                    personas_extras: renta?.personas_extra || 0,
                    horas_extra: renta?.horas_extra || 0,
                    hospedajes_extra: renta?.hospedajes_extra || 0,
                    fecha_entrada: renta?.fecha_registro,
                    fecha_salida: renta?.fecha_salida || renta?.fecha_fin,
                    pagos: renta?.pagos || [],
                    easyrewards_id: renta?.easyrewards_id || renta?.reserva?.easyrewards_id || null,
                    estado_pago: "pagado",
                    experiencias_reserva: [],
                },
            },
        })
    }

    // No show: se hace check out de la renta y la habitación pasa a preparada
    const liberar = async () => {
        const room = habitacionALiberar
        setHabitacionALiberar(null)
        if (!room || !tryStartLoading()) {
            return
        }
        try {
            const { data: dataFecha } = await getCurrentDate()
            const { data } = await checkout({
                variables: {
                    checkOutInput: {
                        renta_id: room?.ultima_renta?.renta_id,
                        usuario_id,
                        estado_habitacion: Estados_Habitaciones.Preparada,
                        fecha_estado: dataFecha?.serverDate,
                    },
                },
            })
            if (!data?.CheckOut) {
                throw new Error("Sin respuesta de check out")
            }
            showSnackbar({
                title: "Habitación disponible para la venta",
                status: "success",
                text: `La habitación **${room?.tipo_habitacion?.nombre} ${room?.numero_habitacion}** pasó de reservada a **preparada**`,
            })
            onLiberada?.()
        } catch {
            showSnackbar({
                title: "Error al liberar habitación",
                status: "error",
                text: "¡Ups! Se ha producido un error. Por favor, inténtalo nuevamente",
            })
        } finally {
            toggleIsLoading({ value: false })
        }
    }

    const LiberarModal = (
        <AuthRequiredModal
            isOpen={!!habitacionALiberar}
            authorizedRoles={LIBERAR_AUTHORIZED_ROLES}
            onAuthFilled={liberar}
            onClose={() => setHabitacionALiberar(null)}
        />
    )

    return {
        canOperate,
        isLoading,
        checkIn,
        // Abre la autorización; al autorizar se libera la habitación indicada
        solicitarLiberar: (room: any) => setHabitacionALiberar(room),
        LiberarModal,
    }
}
